<template>
  <div class="sp-page" :class="[`sp-theme-${style}`, `sp-layout-${layout}`]">
    <div class="sp-wrap">
      <!-- 读取配置中 -->
      <div v-if="status === 'loading'" class="sp-state">
        <span class="sp-spinner" aria-hidden="true" />
        <span>読み込み中…</span>
      </div>

      <!-- 页面不存在 / 已删除 / 停止公开 -->
      <div v-else-if="status === 'notfound'" class="sp-state">
        <strong>ページが見つかりません</strong>
        <span>このページは公開が終了したか、URLが間違っている可能性があります。</span>
      </div>

      <!-- 配置读取失败（网络等） -->
      <div v-else-if="status === 'error'" class="sp-state">
        <strong>ページを読み込めませんでした</strong>
        <span>時間をおいて、もう一度お試しください。</span>
        <button type="button" class="sp-btn" @click="init">再読み込み</button>
      </div>

      <template v-else>
        <!-- 作品信息 -->
        <section class="sp-movie">
          <img v-if="movie?.thumbnail" :src="movie.thumbnail" :alt="movieTitle" class="sp-movie-poster">
          <h1 class="sp-movie-title">{{ movieTitle }}</h1>
          <p v-if="movie?.englishName" class="sp-movie-sub">{{ movie.englishName }}</p>
          <p v-if="openDateText" class="sp-movie-date">{{ openDateText }}</p>
          <p v-if="config.distributorName" class="sp-movie-distributor">配給：{{ config.distributorName }}</p>
        </section>

        <!-- 页面备注（CMS 填写，纯文本保留换行） -->
        <section v-if="config.pageNote" class="sp-page-note">{{ config.pageNote }}</section>

        <!-- 筛选：区域 / 都道府県 / 版本均可多选 -->
        <section class="sp-filters">
          <div class="sp-filter-row">
            <span class="sp-filter-label">エリア</span>
            <div class="sp-chips">
              <button
                v-for="region in regions"
                :key="region.name"
                type="button"
                class="sp-chip"
                :class="{ 'is-on': selRegions.includes(region.name) }"
                :aria-pressed="selRegions.includes(region.name)"
                @click="toggleRegion(region.name)"
              >
                {{ region.name }}
              </button>
            </div>
          </div>
          <div class="sp-filter-row">
            <span class="sp-filter-label">都道府県</span>
            <div v-if="prefOptions.length" class="sp-chips">
              <button
                v-for="pref in prefOptions"
                :key="pref"
                type="button"
                class="sp-chip"
                :class="{ 'is-on': selPrefs.includes(pref) }"
                :aria-pressed="selPrefs.includes(pref)"
                @click="togglePref(pref)"
              >
                {{ pref }}
              </button>
            </div>
            <span v-else class="sp-filter-hint">エリアを選ぶと都道府県で絞り込めます</span>
          </div>
          <div v-if="versionOptions.length" class="sp-filter-row">
            <span class="sp-filter-label">上映形式</span>
            <div class="sp-chips">
              <button
                v-for="version in versionOptions"
                :key="version"
                type="button"
                class="sp-chip"
                :class="{ 'is-on': selVersions.includes(version) }"
                :aria-pressed="selVersions.includes(version)"
                @click="toggleVersion(version)"
              >
                {{ version }}
              </button>
            </div>
          </div>
          <div class="sp-filter-actions">
            <button
              type="button"
              class="sp-btn sp-btn-geo"
              :class="{ 'is-on': !!here }"
              :disabled="geoState === 'locating'"
              :aria-pressed="!!here"
              @click="onClickGeo"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" /></svg>
              {{ geoState === 'locating' ? '取得中…' : here ? '現在地 解除' : '現在地から探す' }}
            </button>
            <button type="button" class="sp-btn sp-btn-clear" :disabled="!hasFilter" @click="clearFilters">
              絞り込みをクリア
            </button>
          </div>
          <p v-if="geoMessage" class="sp-geo-message">{{ geoMessage }}</p>
        </section>

        <!-- 日期条：只在有排期时显示 -->
        <nav v-if="dateStrip.length" class="sp-dates" aria-label="上映日">
          <button
            v-for="d in dateStrip"
            :key="d.date"
            type="button"
            class="sp-date"
            :class="{ 'is-active': d.date === showDate, 'is-sat': d.dowIndex === 6, 'is-sun': d.dowIndex === 0 }"
            :aria-current="d.date === showDate ? 'date' : undefined"
            @click="selectDate(d.date)"
          >
            <span class="sp-date-dow">{{ d.dow }}</span>
            <span class="sp-date-label">{{ d.label }}</span>
          </button>
        </nav>

        <!-- 影院与场次 -->
        <main class="sp-schedule" :class="{ 'is-loading': scheduleLoading }">
          <div v-if="scheduleError" class="sp-empty">
            上映スケジュールを読み込めませんでした。
            <button type="button" class="sp-btn" @click="reloadSchedule">再読み込み</button>
          </div>
          <div v-else-if="scheduleLoading && !sections.length && !tableRegions.length" class="sp-state sp-state-inline">
            <span class="sp-spinner" aria-hidden="true" />
            <span>上映スケジュールを読み込み中…</span>
          </div>
          <div v-else-if="!sections.length && !tableRegions.length" class="sp-empty">
            {{ hasFilter ? '条件に該当する劇場がありません。' : '上映劇場は決まり次第掲載します。' }}
          </div>
          <template v-if="!scheduleError">
            <section v-for="section in sections" :key="section.prefecture" class="sp-pref">
              <h2 class="sp-pref-head">{{ section.prefecture }}</h2>
              <article v-for="row in section.rows" :key="row.cinemaId" class="sp-cinema">
                <div class="sp-cinema-head">
                  <span class="sp-cinema-name">{{ row.name }}</span>
                  <span v-if="row.kmText" class="sp-cinema-dist">{{ row.kmText }}</span>
                  <span v-if="row.versions.length" class="sp-tags">
                    <span v-for="v in row.versions" :key="v" class="sp-tag">{{ v }}</span>
                  </span>
                </div>
                <div v-if="layout === 'list' && row.address" class="sp-cinema-address">{{ row.address }}</div>
                <p v-for="extra in row.extras" :key="extra.title" class="sp-cinema-extra">
                  <span class="sp-cinema-extra-label">{{ extra.title }}</span>{{ extra.value }}
                </p>
                <p v-if="row.note" class="sp-cinema-note">{{ row.note }}</p>

                <!-- EP 返回了影院但没有可解析的场次（兜底） -->
                <p v-if="!row.showings.length" class="sp-cinema-pending">この日の上映スケジュールは未定です。</p>

                <!-- 九宫格：按开始时间分 8 列 -->
                <div v-else-if="layout === 'compact'" class="sp-track">
                  <component
                    :is="s.url ? 'a' : 'span'"
                    v-for="s in row.showings"
                    :key="s.id"
                    class="sp-track-chip"
                    :href="s.url || undefined"
                    :target="s.url ? '_blank' : undefined"
                    :rel="s.url ? 'noopener noreferrer' : undefined"
                    :style="{ gridColumn: s.column + 1, gridRow: s.row + 1 }"
                    :aria-label="`${row.name} ${s.clock}${s.end ? `〜${s.end}` : ''}${s.house ? ` ${s.house}` : ''}`"
                  >
                    <span class="sp-track-start">{{ s.clock }}</span>
                    <span v-if="s.end" class="sp-track-end">〜{{ s.end }}</span>
                  </component>
                </div>

                <!-- 列表：卡片 -->
                <div v-else class="sp-cards">
                  <component
                    :is="s.url ? 'a' : 'span'"
                    v-for="s in row.showings"
                    :key="s.id"
                    class="sp-card"
                    :href="s.url || undefined"
                    :target="s.url ? '_blank' : undefined"
                    :rel="s.url ? 'noopener noreferrer' : undefined"
                  >
                    <span class="sp-card-start">{{ s.clock }}</span>
                    <span v-if="s.end" class="sp-card-end">〜{{ s.end }}</span>
                    <span v-if="s.house" class="sp-card-house">{{ s.house }}</span>
                  </component>
                </div>
              </article>
            </section>

            <!-- 手选了但当前没有场次的影院：按地区分组的表格（整部作品没有场次时只显示这里） -->
            <section v-if="tableRegions.length" class="sp-table-block">
              <h2 v-if="sections.length" class="sp-table-block-title">その他の上映予定劇場</h2>
              <section v-for="region in tableRegions" :key="region.name" class="sp-region">
                <h3 class="sp-region-head">{{ region.title }}</h3>
                <table class="sp-table">
                  <thead>
                    <tr>
                      <th class="col-pref">都道府県</th>
                      <th class="col-name">劇場名</th>
                      <th class="col-tel">電話番号</th>
                      <th v-for="title in extraColumns" :key="title" class="col-extra">{{ title }}</th>
                      <th class="col-note">備考</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in region.rows" :key="row.cinemaId">
                      <td class="col-pref" data-label="都道府県">{{ row.prefecture }}</td>
                      <td class="col-name" data-label="劇場名">
                        <a v-if="row.url" :href="row.url" target="_blank" rel="noopener noreferrer">{{ row.name }}</a>
                        <template v-else>{{ row.name }}</template>
                      </td>
                      <td class="col-tel" data-label="電話番号">
                        <a v-if="row.telephone" :href="`tel:${row.telephone.replace(/[^\d+]/g, '')}`">{{ row.telephone }}</a>
                      </td>
                      <td
                        v-for="(title, i) in extraColumns"
                        :key="title"
                        class="col-extra"
                        :data-label="title"
                      >{{ row.extraValues[i] }}</td>
                      <td class="col-note" data-label="備考">{{ row.note }}</td>
                    </tr>
                  </tbody>
                </table>
              </section>
            </section>
          </template>
        </main>

        <!-- 注意事项 -->
        <section class="sp-notes">
          <ul>
            <li>上映劇場・時間が変更となる場合がありますので、鑑賞の前に必ず劇場にてご確認ください。</li>
            <li>都道府県ごとに定められた「青少年育成条例」により、18歳未満の方は夜間入場不可となることがあります。</li>
            <li>上映時間のデータは「映画館に行こう!」による提供及び、映画ランドにて独自調査したものです。</li>
          </ul>
        </section>
      </template>
    </div>

    <button
      type="button"
      class="sp-back-top"
      :class="{ 'is-visible': showBackTop }"
      aria-label="ページの先頭へ戻る"
      @click="scrollToTop"
    >
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><polyline points="18 15 12 9 6 15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
    </button>
  </div>
</template>

<script setup>
/**
 * 作品上映排期页・观众页（DEV-15088）
 * 配给公司把链接放在电影官网上；配置（配色 / 显示方式 / 备注 / 手选影院）来自 DP，
 * 排期数据由浏览器直连 EP /web/** 接口获取。
 */
import {
  computed, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { getPublicSchedulePageService } from '@/api/dp/schedulePage';
import {
  fetchEpAreas, fetchEpCinemaList, fetchEpMovieDetail, fetchEpMovieSchedules, fetchEpScheduleDates,
} from '@/api/dp/epWeb';
import {
  clockOf, dateLabel, distanceKm, endClockOf, formatKm, formatOpenDate, normalizeLayout,
  normalizeStyle, placeOnTrack, todayInJapan,
} from '../constants';

const route = useRoute();

// ---------- 页面配置 ----------
const status = ref('loading'); // loading | notfound | error | ready
const config = ref({});
const movie = ref(null);
const style = computed(() => normalizeStyle(config.value.style));
const layout = computed(() => normalizeLayout(config.value.layout));
const movieTitle = computed(() => movie.value?.name || config.value.movieTitle || '');
const openDateText = computed(() => formatOpenDate(movie.value?.openDate));

// ---------- 区域树与影院主数据 ----------
const regions = ref([]); // [{ name, prefectures: [name] }]
const cinemaMap = ref(new Map()); // eglCinemaId → EP 影院信息
// 都道府県 → 在区域树里的顺序，用于分组排序
const prefOrder = computed(() => {
  const order = new Map();
  regions.value.forEach((r) => r.prefectures.forEach((p) => order.set(p, order.size)));
  return order;
});
const allPrefectures = computed(() => regions.value.flatMap((r) => r.prefectures));

// ---------- 筛选 ----------
const selRegions = ref([]);
const selPrefs = ref([]);
const selVersions = ref([]);
const hasFilter = computed(() => selRegions.value.length > 0 || selPrefs.value.length > 0 || selVersions.value.length > 0 || !!here.value);

// 都道府県选项：只展示已选区域下的
const prefOptions = computed(() => regions.value
  .filter((r) => selRegions.value.includes(r.name))
  .flatMap((r) => r.prefectures));

/**
 * 实际查询用的都道府県列表
 * prefectureList 为空时 EP 默认只查东京都，所以「全国」和「只选区域」都要展开成具体的都道府県
 */
const effectivePrefs = computed(() => {
  if (selPrefs.value.length) return selPrefs.value;
  if (selRegions.value.length) return prefOptions.value;
  return allPrefectures.value;
});
const prefsKey = computed(() => effectivePrefs.value.join(','));

function toggleIn(listRef, value) {
  listRef.value = listRef.value.includes(value)
    ? listRef.value.filter((v) => v !== value)
    : [...listRef.value, value];
}

function toggleRegion(name) {
  toggleIn(selRegions, name);
  // 取消区域时，同时去掉该区域下已选的都道府県
  const visible = new Set(prefOptions.value);
  selPrefs.value = selPrefs.value.filter((p) => visible.has(p));
}

function togglePref(name) {
  toggleIn(selPrefs, name);
}

function toggleVersion(name) {
  toggleIn(selVersions, name);
}

function clearFilters() {
  selRegions.value = [];
  selPrefs.value = [];
  selVersions.value = [];
  here.value = null;
  geoMessage.value = '';
}

// ---------- 日期与场次 ----------
const dates = ref([]);
const showDate = ref(null);
const groups = ref([]); // 当天 EP 返回的按影院分组的场次
const scheduleLoading = ref(false);
const scheduleError = ref(false);
const today = todayInJapan();
const dateStrip = computed(() => dates.value.map((date) => ({ date, ...dateLabel(date, today) })));

// (日期|都道府県) → 场次；切换版本、来回切换日期时不重复请求
const scheduleCache = new Map();
// 只采用最新一次请求的结果，避免慢响应覆盖新筛选
let datesRun = 0;
let scheduleRun = 0;

async function loadDates() {
  const eglMovieId = config.value.eglMovieId;
  const prefectureList = effectivePrefs.value;
  const mine = ++datesRun;
  scheduleError.value = false;
  if (!eglMovieId || !prefectureList.length) {
    dates.value = [];
    showDate.value = null;
    groups.value = [];
    return;
  }
  scheduleLoading.value = true;
  try {
    const list = await fetchEpScheduleDates({ movieId: eglMovieId, prefectureList });
    if (mine !== datesRun) return;
    // 过滤掉今天之前的日期（EP 正常不会返回，这里兜底）
    dates.value = (Array.isArray(list) ? list : []).filter((d) => d >= today);
    if (!dates.value.includes(showDate.value)) showDate.value = dates.value[0] ?? null;
    await loadSchedules();
  } catch (e) {
    if (mine !== datesRun) return;
    console.error(e);
    scheduleError.value = true;
    scheduleLoading.value = false;
  }
}

async function loadSchedules() {
  const date = showDate.value;
  const prefectureList = effectivePrefs.value;
  const mine = ++scheduleRun;
  scheduleError.value = false;
  if (!date) {
    groups.value = [];
    scheduleLoading.value = false;
    return;
  }
  const key = `${date}|${prefectureList.join(',')}`;
  if (scheduleCache.has(key)) {
    groups.value = scheduleCache.get(key);
    scheduleLoading.value = false;
    return;
  }
  scheduleLoading.value = true;
  try {
    const data = await fetchEpMovieSchedules({
      movieId: config.value.eglMovieId, showDate: date, prefectureList, cityList: [],
    });
    if (mine !== scheduleRun) return;
    const list = Array.isArray(data) ? data : [];
    scheduleCache.set(key, list);
    groups.value = list;
  } catch (e) {
    if (mine !== scheduleRun) return;
    console.error(e);
    scheduleError.value = true;
  } finally {
    if (mine === scheduleRun) scheduleLoading.value = false;
  }
}

function selectDate(date) {
  if (date === showDate.value) return;
  showDate.value = date;
  loadSchedules();
}

function reloadSchedule() {
  // 失败时可能是日期列表没取到，也可能只是当天场次没取到
  if (dates.value.length) loadSchedules();
  else loadDates();
}

// 都道府県范围变化 → 重新取日期和场次；不在初始化完成前触发
watch(prefsKey, () => {
  if (status.value === 'ready') loadDates();
});

// 版本选项：当天出现过的版本 + 已选中的（换日期后已选项不会消失）
const versionOptions = computed(() => {
  const set = new Set();
  groups.value.forEach((g) => (g.version || []).forEach((v) => set.add(v)));
  selVersions.value.forEach((v) => set.add(v));
  return [...set];
});

// ---------- 现在地 ----------
const here = ref(null); // { lat, lon }
const geoState = ref('idle'); // idle | locating
const geoMessage = ref('');

function requestPosition() {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => resolve({ lat: coords.latitude, lon: coords.longitude }),
      reject,
      { enableHighAccuracy: false, maximumAge: 600000, timeout: 15000 },
    );
  });
}

/**
 * 点击「現在地から探す」：定位 → 找最近的影院 → 按它所在的都道府県筛选，并按距离排序
 * 再次点击解除；用户拒绝或定位失败时只给提示，不影响浏览
 */
async function onClickGeo() {
  geoMessage.value = '';
  if (here.value) {
    here.value = null;
    return;
  }
  if (!navigator.geolocation) {
    geoMessage.value = 'お使いのブラウザは位置情報に対応していません。';
    return;
  }
  geoState.value = 'locating';
  try {
    const pos = await requestPosition();
    here.value = pos;
    const nearest = nearestCinema(pos);
    const pref = nearest?.prefecture;
    const region = regions.value.find((r) => r.prefectures.includes(pref));
    if (pref && region) {
      selRegions.value = [region.name];
      selPrefs.value = [pref];
    }
  } catch (err) {
    geoMessage.value = err?.code === 1
      ? '位置情報の利用が許可されていません。ブラウザまたは端末の設定から許可してください。'
      : '現在地を取得できませんでした。';
  } finally {
    geoState.value = 'idle';
  }
}

// 影院主数据中离当前位置最近的一家
function nearestCinema(pos) {
  let best = null;
  let bestKm = Infinity;
  cinemaMap.value.forEach((c) => {
    const km = distanceKm(pos, c.lat, c.lon);
    if (km < bestKm) {
      best = c;
      bestKm = km;
    }
  });
  return best;
}

// ---------- 展示数据 ----------
// 追加列的列名（最多 3 列，数组顺序即显示顺序；如「日本語字幕付き上映」）
const extraColumns = computed(() => (Array.isArray(config.value.extraColumns) ? config.value.extraColumns : [])
  .map((title) => String(title ?? '').trim())
  .filter(Boolean));

// 手选影院：eglCinemaId → 配置顺序与 DP 返回的附加信息（字段为空时后端返回 null）
const manualMap = computed(() => {
  const map = new Map();
  (config.value.cinemas || []).forEach((c, index) => {
    map.set(Number(c.eglCinemaId), {
      order: index,
      note: c.note || '',
      cinemaName: c.cinemaName || '',
      // 与 extraColumns 按下标一一对应，没填的位置为 null
      extraValues: extraColumns.value.map((_, i) => (Array.isArray(c.extraValues) ? c.extraValues[i] ?? '' : '')),
      // 公开接口已按「保存值 > DP 默认值」处理；为空时再用 EP 兜底
      telephone: c.telephone || '',
      hpUrl: c.hpUrl || '',
    });
  });
  return map;
});

/**
 * 按都道府県分组的影院行（九宫格 / 列表），只包含当天有场次的影院
 * - EP 返回的影院（按版本筛选），手选的带上备注、并排在前面（按配置顺序）
 * - 有现在地时按距离排序
 */
const sections = computed(() => {
  const duration = Number(movie.value?.duration) || 0;
  const rows = [];

  groups.value.forEach((g, apiIndex) => {
    const id = Number(g.cinemaId);
    const versions = g.version || [];
    if (selVersions.value.length && !versions.some((v) => selVersions.value.includes(v))) return;
    const info = cinemaMap.value.get(id);
    const manual = manualMap.value.get(id);
    const showings = (g.schedules || []).flatMap((s) => {
      const clock = clockOf(s.showStartTime);
      if (!clock) return [];
      return [{
        id: s.id, clock, end: endClockOf(s, duration), url: s.url || '', house: s.house || '',
      }];
    });
    rows.push({
      cinemaId: id,
      name: g.name || info?.name || manual?.cinemaName || '',
      prefecture: info?.prefecture || 'その他',
      address: info?.address || '',
      lat: g.lat ?? info?.lat,
      lon: g.lon ?? info?.lon,
      versions,
      note: manual?.note || '',
      // 九宫格里每个有值的追加列显示一行「列名 值」
      extras: manual
        ? extraColumns.value
          .map((title, i) => ({ title, value: manual.extraValues[i] }))
          .filter((extra) => extra.value)
        : [],
      showings: placeOnTrack(showings),
      // 排序键：手选的按配置顺序在前，其余按 EP 返回顺序
      sortKey: manual ? manual.order : 100000 + apiIndex,
    });
  });

  rows.forEach((row) => {
    row.km = distanceKm(here.value, row.lat, row.lon);
    row.kmText = here.value ? formatKm(row.km) : '';
  });

  const byPref = new Map();
  rows.forEach((row) => {
    if (!byPref.has(row.prefecture)) byPref.set(row.prefecture, []);
    byPref.get(row.prefecture).push(row);
  });
  const list = [...byPref.entries()].map(([prefecture, prefRows]) => {
    prefRows.sort((a, b) => (here.value ? a.km - b.km : 0) || a.sortKey - b.sortKey);
    return { prefecture, rows: prefRows, minKm: prefRows[0].km };
  });
  const orderOf = (p) => prefOrder.value.get(p) ?? Infinity;
  list.sort((a, b) => (here.value ? a.minKm - b.minKm : 0) || orderOf(a.prefecture) - orderOf(b.prefecture));
  return list;
});

/**
 * 手选了但当前没有场次的影院：按地区分组的表格
 * - 当天 EP 返回了场次的影院不进表格（它们在九宫格里）；版本筛选不作用于表格
 * - 区域 / 都道府県筛选作用于表格；不在 EP 影院主数据里的影院不知道所在地，只在没有地域筛选时显示
 * - 地区顺序与区域树（筛选按钮）一致，地区内按 CMS 保存的顺序
 */
const tableRegions = computed(() => {
  const scheduled = new Set(groups.value.map((g) => Number(g.cinemaId)));
  const areaFiltered = selRegions.value.length > 0 || selPrefs.value.length > 0;
  const prefSet = new Set(effectivePrefs.value);
  const regionOfPref = new Map();
  regions.value.forEach((r) => r.prefectures.forEach((p) => regionOfPref.set(p, r.name)));

  const byRegion = new Map();
  manualMap.value.forEach((manual, id) => {
    if (scheduled.has(id)) return;
    const info = cinemaMap.value.get(id);
    if (areaFiltered && !(info && prefSet.has(info.prefecture))) return;
    const region = info?.region || regionOfPref.get(info?.prefecture) || 'その他';
    if (!byRegion.has(region)) byRegion.set(region, []);
    byRegion.get(region).push({
      cinemaId: id,
      prefecture: info?.prefecture || '',
      name: info?.name || manual.cinemaName,
      // 影院官网：优先 EP 的 url，没有时用 DP 的 hpUrl
      url: info?.url || manual.hpUrl,
      // 电话：DP 覆盖率高，优先 DP，没有时用 EP 的
      // 电话：保存值 > DP 默认值（公开接口已处理）> EP 的电话
      telephone: manual.telephone || info?.telephone || '',
      extraValues: manual.extraValues,
      note: manual.note,
      order: manual.order,
    });
  });

  const regionOrder = new Map(regions.value.map((r, i) => [r.name, i]));
  return [...byRegion.entries()]
    .map(([name, rows]) => ({
      name,
      title: name === 'その他' ? name : `${name}地区`,
      rows: rows.sort((a, b) => a.order - b.order),
    }))
    .sort((a, b) => (regionOrder.get(a.name) ?? Infinity) - (regionOrder.get(b.name) ?? Infinity));
});

// ---------- 初始化 ----------
async function init() {
  status.value = 'loading';
  const pageKey = String(route.params.pageKey || '');
  try {
    config.value = await getPublicSchedulePageService(pageKey);
  } catch (e) {
    status.value = e?.status === 404 ? 'notfound' : 'error';
    return;
  }
  document.title = `${config.value.movieTitle || ''}｜上映スケジュール`;

  // 作品详情、区域树、影院主数据都允许单独失败：页面用配置里的快照兜底，不整体报错
  const [movieRes, areasRes, cinemasRes] = await Promise.allSettled([
    fetchEpMovieDetail(config.value.eglMovieId),
    fetchEpAreas(),
    fetchEpCinemaList(),
  ]);
  movie.value = movieRes.status === 'fulfilled' ? movieRes.value : null;
  if (movie.value?.name) document.title = `${movie.value.name}｜上映スケジュール`;

  const cinemas = cinemasRes.status === 'fulfilled' && Array.isArray(cinemasRes.value) ? cinemasRes.value : [];
  cinemaMap.value = new Map(cinemas.map((c) => [Number(c.id), c]));

  const tree = areasRes.status === 'fulfilled' && Array.isArray(areasRes.value) ? areasRes.value : [];
  regions.value = tree
    .map((r) => ({ name: r.name, prefectures: (r.children || []).map((p) => p.name) }))
    .filter((r) => r.prefectures.length > 0);
  // 区域树取不到时，用影院主数据里出现过的都道府県兜底，至少保证能查全国
  if (!regions.value.length) {
    const prefs = [...new Set(cinemas.map((c) => c.prefecture).filter(Boolean))];
    if (prefs.length) regions.value = [{ name: '全国', prefectures: prefs }];
  }

  status.value = 'ready';
  loadDates();
  fillDistanceIfGranted();
}

// 已经授权过定位时，静默取一次位置用于显示距离（不改变筛选条件）
function fillDistanceIfGranted() {
  if (!navigator.geolocation || !navigator.permissions?.query) return;
  navigator.permissions.query({ name: 'geolocation' })
    .then((perm) => {
      if (perm.state !== 'granted') return;
      requestPosition().then((pos) => {
        if (!here.value) here.value = pos;
      }).catch(() => {});
    })
    .catch(() => {});
}

// ---------- 回到顶部 ----------
const showBackTop = ref(false);
function onScroll() {
  showBackTop.value = window.scrollY > 400;
}
function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

// 页面背景跟随配色：滚动回弹或内容不足一屏时 body 底色也不会露出来
const bodyBgBackup = document.body.style.background;
watch([style, status], () => {
  const el = document.querySelector('.sp-page');
  if (el) document.body.style.background = getComputedStyle(el).getPropertyValue('--sp-bg').trim();
}, { flush: 'post', immediate: true });

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true });
  init();
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
  document.body.style.background = bodyBgBackup;
});
</script>

<style lang="scss">
@import '../themes.scss';
</style>

<style lang="scss" scoped>
.sp-page {
  min-height: 100vh;
  background: var(--sp-bg);
  color: var(--sp-text);
  font-family: -apple-system, BlinkMacSystemFont, "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  -webkit-text-size-adjust: 100%;

  *, *::before, *::after { box-sizing: border-box; }
  button { font-family: inherit; }
}

.sp-wrap {
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  padding-bottom: 32px;
}

/* ---------- 状态 ---------- */
.sp-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 60vh;
  padding: 24px 16px;
  text-align: center;
  color: var(--sp-text-soft);

  strong { font-size: 18px; color: var(--sp-text); }
}
.sp-state-inline { min-height: 160px; }
.sp-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--sp-border);
  border-top-color: var(--sp-accent);
  border-radius: 50%;
  animation: sp-spin 0.8s linear infinite;
}
@keyframes sp-spin { to { transform: rotate(360deg); } }

.sp-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  font-size: 13px;
  color: var(--sp-text);
  background: var(--sp-surface);
  border: 1px solid var(--sp-border-input);
  border-radius: 999px;
  cursor: pointer;

  &:disabled { opacity: 0.45; cursor: default; }
  &.is-on {
    background: var(--sp-accent);
    color: var(--sp-accent-on);
    border-color: var(--sp-accent);
  }
}

/* ---------- 作品信息 ---------- */
.sp-movie {
  padding: 20px 16px 16px;
  text-align: center;
}
.sp-movie-poster {
  display: block;
  width: 100%;
  max-width: 280px;
  height: auto;
  margin: 0 auto 14px;
  border-radius: 4px;
}
.sp-movie-title {
  margin: 0 0 4px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 0.06em;
  overflow-wrap: anywhere;
}
.sp-movie-sub {
  margin: 0 0 6px;
  font-size: 13px;
  color: var(--sp-text-muted);
  letter-spacing: 0.15em;
}
.sp-movie-date {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}
.sp-movie-distributor {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--sp-text-softer);
}

.sp-page-note {
  margin: 0 16px 16px;
  padding: 14px 16px;
  background: var(--sp-surface);
  border: 1px solid var(--sp-border-light);
  border-radius: 4px;
  font-size: 13px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

/* ---------- 筛选 ---------- */
.sp-filters {
  padding: 4px 16px 12px;
  border-bottom: 1px solid var(--sp-border);
}
.sp-filter-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px dotted var(--sp-border-input);
}
.sp-filter-label {
  flex: 0 0 64px;
  padding-top: 6px;
  font-size: 13px;
  font-weight: 600;
}
.sp-filter-hint {
  padding-top: 6px;
  font-size: 12px;
  color: var(--sp-text-light);
}
.sp-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}
.sp-chip {
  padding: 6px 12px;
  font-size: 13px;
  line-height: 1.2;
  color: var(--sp-text-muted);
  background: var(--sp-bg);
  border: 1px solid var(--sp-border-input);
  border-radius: 4px;
  cursor: pointer;

  &.is-on {
    background: var(--sp-primary);
    color: var(--sp-primary-on);
    border-color: var(--sp-primary);
    font-weight: 600;
  }
}
.sp-filter-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 12px;
}
.sp-geo-message {
  margin: 8px 0 0;
  font-size: 12px;
  color: var(--sp-text-soft);
}

/* ---------- 日期条 ---------- */
.sp-dates {
  display: flex;
  overflow-x: auto;
  border-bottom: 1px solid var(--sp-border);
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar { display: none; }
}
.sp-date {
  flex: 0 0 76px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60px;
  padding: 8px 4px;
  background: transparent;
  border: none;
  color: var(--sp-text-soft);
  cursor: pointer;
  touch-action: manipulation;

  .sp-date-dow { font-size: 12px; color: var(--sp-text-light); }
  .sp-date-label { font-size: 15px; font-weight: 700; }
  &.is-sat .sp-date-dow { color: #3b78d8; }
  &.is-sun .sp-date-dow { color: #d8433b; }
  &.is-active {
    background: var(--sp-primary);
    color: var(--sp-primary-on);
    clip-path: polygon(14px 0, 100% 0, calc(100% - 14px) 100%, 0 100%);

    .sp-date-dow { color: var(--sp-primary-on); }
  }
}

/* ---------- 影院与场次 ---------- */
.sp-schedule {
  padding: 8px 16px 16px;
  transition: opacity 0.15s;

  &.is-loading { opacity: 0.5; pointer-events: none; }
}
.sp-empty {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  color: var(--sp-text-softer);
  font-size: 13px;
}
.sp-pref-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 20px 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: var(--sp-text-soft);

  &::before {
    content: "";
    width: 4px;
    height: 18px;
    background: var(--sp-bar);
    border-radius: 2px;
  }
}
.sp-cinema {
  padding: 14px 0;
  border-bottom: 1px solid var(--sp-border);

  &:last-child { border-bottom: none; }
}
.sp-cinema-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
}
.sp-cinema-name {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.02em;
}
.sp-cinema-dist {
  font-size: 12px;
  color: var(--sp-text-light);
}
.sp-tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-left: auto;
}
.sp-tag {
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  color: var(--sp-text-muted);
  border: 1px solid var(--sp-border-input);
  border-radius: 4px;
}
.sp-cinema-address {
  margin-top: 4px;
  font-size: 12px;
  color: var(--sp-text-softer);
}
.sp-cinema-note {
  margin: 8px 0 0;
  padding: 6px 10px;
  font-size: 12px;
  color: var(--sp-text-muted);
  background: var(--sp-surface);
  border-left: 3px solid var(--sp-accent);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.sp-cinema-pending {
  margin: 10px 0 0;
  font-size: 13px;
  color: var(--sp-text-softer);
}
.sp-cinema-extra {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--sp-text-muted);
}
.sp-cinema-extra-label {
  margin-right: 6px;
  padding: 1px 6px;
  font-weight: 600;
  color: var(--sp-accent-on);
  background: var(--sp-accent);
  border-radius: 3px;
}

/* ---------- 没有场次的影院：按地区分组的表格 ---------- */
.sp-table-block { margin-top: 8px; }
.sp-table-block-title {
  margin: 28px 0 0;
  padding-bottom: 8px;
  font-size: 17px;
  font-weight: 800;
  border-bottom: 2px solid var(--sp-bar);
}
.sp-region-head {
  margin: 24px 0 10px;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.04em;
}
.sp-table {
  width: 100%;
  border-collapse: collapse;
  // 各地区的表格列宽保持一致
  table-layout: fixed;
  font-size: 13px;

  th.col-pref { width: 88px; }
  th.col-name { width: 28%; }
  th.col-tel { width: 132px; }

  th, td {
    padding: 10px 12px;
    border: 1px solid var(--sp-border);
    text-align: left;
    vertical-align: top;
  }
  th {
    font-weight: 600;
    white-space: nowrap;
    color: var(--sp-text-muted);
    background: var(--sp-surface);
  }
  td.col-pref, td.col-tel { white-space: nowrap; }
  td.col-name { font-weight: 600; }
  td.col-extra, td.col-note {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  a { color: var(--sp-link); }
}

/* 手机宽度：每家影院一张卡片，用 data-label 显示列名 */
@media (max-width: 639px) {
  .sp-table {
    thead { display: none; }
    tr {
      // 卡片里劇場名放在最上面
      display: flex;
      flex-direction: column;
      padding-bottom: 4px;
      margin-bottom: 10px;
      border: 1px solid var(--sp-border);
      border-radius: 6px;
      background: var(--sp-surface-card);
    }
    td {
      display: grid;
      grid-template-columns: 6.5em 1fr;
      gap: 8px;
      padding: 6px 12px;
      border: none;
      white-space: normal;

      &::before {
        content: attr(data-label);
        font-size: 12px;
        color: var(--sp-text-light);
      }
      &:empty { display: none; }
    }
    td.col-pref, td.col-tel { white-space: normal; }
    td.col-name {
      order: -1;
      grid-template-columns: 1fr;
      padding-top: 10px;
      font-size: 15px;

      &::before { display: none; }
    }
  }
}

/* 九宫格：8 列等宽，同一时间段的场次往下叠 */
.sp-track {
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  gap: clamp(3px, 0.9vw, 8px);
  margin-top: 12px;
}
.sp-track-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 6px 2px;
  background: var(--sp-surface-card);
  border: 1px solid var(--sp-border);
  border-radius: 6px;
  color: var(--sp-accent);
  text-decoration: none;
  line-height: 1.2;
}
a.sp-track-chip:hover {
  border-color: var(--sp-accent);
  background: var(--sp-accent-hover-bg);
}
.sp-track-start {
  font-size: clamp(12px, 3.4vw, 17px);
  font-weight: 800;
}
.sp-track-end {
  margin-top: 2px;
  // 窄屏时一格只有 40px 左右，结束时间不换行
  white-space: nowrap;
  letter-spacing: -0.02em;
  font-size: clamp(8px, 2.3vw, 12px);
  color: var(--sp-text-soft);
}

/* 列表：卡片 */
.sp-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}
.sp-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 96px;
  padding: 10px 12px;
  background: var(--sp-surface-card);
  border: 1px solid var(--sp-border);
  border-radius: 8px;
  color: var(--sp-text);
  text-decoration: none;
}
a.sp-card:hover {
  border-color: var(--sp-accent);
  background: var(--sp-accent-hover-bg);
}
.sp-card-start {
  font-size: 20px;
  font-weight: 800;
  color: var(--sp-accent);
}
a.sp-card .sp-card-start { text-decoration: underline; }
.sp-card-end {
  font-size: 13px;
  color: var(--sp-text-soft);
}
.sp-card-house {
  margin-top: 2px;
  font-size: 11px;
  color: var(--sp-text-light);
}

/* ---------- 注意事项 ---------- */
.sp-notes {
  margin: 8px 16px 0;
  padding: 12px 16px;
  background: var(--sp-surface);
  border: 1px solid var(--sp-border-light);
  border-radius: 4px;
  font-size: 12px;
  color: var(--sp-text-soft);

  ul { margin: 0; padding-left: 1.2em; }
  li + li { margin-top: 4px; }
}

/* ---------- 回到顶部 ---------- */
.sp-back-top {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 90;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: var(--sp-accent);
  color: var(--sp-accent-on);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  opacity: 0;
  transform: translateY(8px);
  pointer-events: none;
  transition: opacity 0.2s, transform 0.2s;

  &.is-visible {
    opacity: 1;
    transform: none;
    pointer-events: auto;
  }
}

@media (min-width: 640px) {
  .sp-page { font-size: 15px; }
  .sp-movie-title { font-size: 32px; }
  .sp-cinema-name { font-size: 20px; }
  .sp-filters, .sp-schedule { padding-left: 24px; padding-right: 24px; }
  .sp-page-note, .sp-notes { margin-left: 24px; margin-right: 24px; }
  .sp-date { flex-basis: 96px; }
}
</style>
